'use client';

// Vidéo d'une animation, IMAGE PAR IMAGE (lot 7 : YouTube Shorts, TikTok,
// Reels). Même scène, mêmes calques que le lecteur (accroche, intro,
// générique, pastille HORA, sous-titres), mais sans horloge ni voix :
// `seek(ms)` dessine l'image de l'instant `ms` du plan (`videoFrameAt`). Le
// worker vidéo appelle `seek` pour chaque image et en fait une capture ;
// FFmpeg pose les voix aux instants du même plan.
//
// Rien ne bouge tout seul : pas de transition CSS (la capture d'une image
// ne doit pas dépendre de la vitesse de la machine), la pastille s'efface
// selon le temps de la scène. Les questions sont jouées en démonstration
// (`VideoGatePanel`).

import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  JRAF_PALETTE,
  mouthFrameAt,
  outroMessage,
  PANEL_GEOMETRY,
  sceneSpeaker,
  sceneStateAt,
  sceneVoice,
  videoFrameAt,
  videoVoiceAt,
  voiceSpeakers,
  type CompiledAnimation,
  type VideoFrame,
  type VideoPlan,
} from '@jeraisonne/animation';
import { levelLabel, subjectLabel } from '@jeraisonne/db/curriculum';
import { cn } from '../../lib/utils';
import { AnimationMathText } from './animation-math';
import { BookendLayer, type BookendHandle } from './bookend-layer';
import { HookLayer } from './hook-layer';
import { bookendHosts, SpeakerAvatar, usableAvatar, type SpeakerAvatars } from './speaker-avatar';
import { SCENE_TEXT } from './scene-sizes';
import { AnimationStage, type StageHandle } from './stage';
import { VideoGatePanel } from './video-panels';

export interface AnimationVideoHandle {
  /** dessine l'image de l'instant `ms` de la vidéo (synchrone) */
  seek(ms: number): VideoFrame;
}

export interface AnimationVideoProps {
  /** `compileAnimation(videoDocument(doc))` : voir `planVideo` */
  anim: CompiledAnimation;
  plan: VideoPlan;
  /** URL d'une ressource du bucket (images) */
  assetUrl?: (path: string) => string;
  /** avatars animés des comptes qui ont enregistré les voix (`speakerId`) */
  speakerAvatars?: SpeakerAvatars | null;
  className?: string;
}

const HORA_LABEL: Record<string, string> = {
  H: 'Hypothèse',
  O: 'Outil',
  R: 'Raisonnement',
  A: 'Application',
};
const HORA_COLOR: Record<string, string> = {
  H: JRAF_PALETTE['hora-h'],
  O: JRAF_PALETTE['hora-o'],
  R: JRAF_PALETTE['hora-r'],
  A: JRAF_PALETTE['hora-a'],
};
const HORA_TEXT: Record<string, string> = {
  H: JRAF_PALETTE.paper,
  O: JRAF_PALETTE.night,
  R: JRAF_PALETTE.night,
  A: JRAF_PALETTE.paper,
};

const NO_MOTION_CSS =
  '[data-jr-video] *, [data-jr-video] *::before, [data-jr-video] *::after { transition: none !important; animation: none !important; }';

export const AnimationVideo = forwardRef<AnimationVideoHandle, AnimationVideoProps>(
  function AnimationVideo({ anim, plan, assetUrl, speakerAvatars, className }, ref) {
    const doc = anim.doc;
    const info = useMemo(() => {
      const level = doc.metadata.levels[0];
      const meta = [level ? levelLabel(level) : '', subjectLabel(doc.metadata.subject)]
        .filter(Boolean)
        .join(' · ');
      return {
        meta: meta || null,
        outro: outroMessage(doc, 'video'),
        hook: doc.bookends?.hook ?? null,
        speakers: voiceSpeakers(doc),
      };
    }, [doc]);
    const stageRef = useRef<StageHandle>(null);
    const bookendRef = useRef<BookendHandle>(null);
    const [frame, setFrame] = useState<VideoFrame>(() => videoFrameAt(plan, 0));
    /** instant de la vidéo (bouche de l'avatar) */
    const [atMs, setAtMs] = useState(0);
    const hostAvatars = useMemo(() => bookendHosts(doc, speakerAvatars), [doc, speakerAvatars]);

    useImperativeHandle(
      ref,
      () => ({
        seek(ms: number) {
          const f = videoFrameAt(plan, ms);
          // calques (scène, sous-titre, question) écrits AVANT la capture
          flushSync(() => {
            setFrame(f);
            setAtMs(ms);
          });
          const sc = anim.scenes[f.scene]!;
          stageRef.current?.apply(sceneStateAt(sc, f.sceneMs));
          bookendRef.current?.apply(f.sceneMs);
          return f;
        },
      }),
      [anim, plan],
    );

    const sc = anim.scenes[frame.scene] ?? anim.scenes[0]!;
    const kind = frame.kind;
    const gateStep = frame.gate
      ? (sc.scene.steps.find((s) => s.id === frame.gate!.plan.stepId) ?? null)
      : null;
    const hora = kind === 'content' ? sc.scene.hora : undefined;
    // avatar de qui a enregistré la voix de la scène ; bouche selon l'instant de la voix
    const sceneAvatar =
      kind === 'content' ? usableAvatar(speakerAvatars, sceneSpeaker(doc, sc.scene.id)) : null;
    let mouth = 0;
    if (sceneAvatar) {
      const asset = sceneVoice(doc, sc.scene.id);
      const v = videoVoiceAt(plan, atMs);
      mouth = asset && v && v.assetId === asset.id ? mouthFrameAt(asset.words, v.fileMs, true) : 0;
    }

    const overlay = (
      <>
        {kind === 'hook' && info.hook && (
          <HookLayer
            key="hook"
            ref={bookendRef}
            text={info.hook.text}
            emphasis={info.hook.emphasis}
            reducedMotion={false}
          />
        )}
        {(kind === 'intro' || kind === 'outro') && (
          <BookendLayer
            key={kind}
            ref={bookendRef}
            kind={kind}
            title={doc.title}
            meta={info.meta}
            message={info.outro}
            speakers={info.speakers}
            hosts={hostAvatars}
            reducedMotion={false}
          />
        )}

        {kind === 'content' && frame.chipOpacity > 0 && (
          <div
            className={cn(
              'absolute left-4 top-4 flex items-center',
              sceneAvatar ? 'max-w-[calc(100%-7rem)]' : 'max-w-[85%]',
            )}
          >
            <div
              data-video-chip=""
              className="flex min-w-0 max-w-full items-center gap-2 rounded-full py-1.5 pl-1.5 pr-4 text-[15px] font-semibold"
              style={{
                backgroundColor: 'rgba(0,0,0,0.45)',
                color: JRAF_PALETTE.paper,
                opacity: frame.chipOpacity,
              }}
            >
              {hora && (
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[14px] font-bold"
                  style={{
                    backgroundColor: HORA_COLOR[hora],
                    color: HORA_TEXT[hora],
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
                  }}
                  title={HORA_LABEL[hora]}
                >
                  {hora}
                </span>
              )}
              <span className="truncate">
                <AnimationMathText>{sc.scene.title}</AnimationMathText>
              </span>
            </div>
          </div>
        )}

        {sceneAvatar && (
          <div className="absolute right-4 top-4">
            <SpeakerAvatar frames={sceneAvatar.frames} frame={mouth} />
          </div>
        )}

        {frame.caption && (
          <div
            data-video-caption={frame.caption.index}
            className="absolute flex justify-center"
            style={{
              left: frame.caption.left,
              right: frame.caption.right,
              bottom: frame.caption.bottom,
            }}
          >
            <p
              className={cn('rounded-xl px-3 py-2 text-center leading-snug', SCENE_TEXT)}
              style={{ backgroundColor: 'rgba(0,0,0,0.62)', color: JRAF_PALETTE.paper }}
            >
              <AnimationMathText>{frame.caption.text}</AnimationMathText>
            </p>
          </div>
        )}

        {frame.gate && gateStep && (
          <VideoGatePanel
            step={gateStep}
            gate={frame.gate.plan}
            revealed={frame.gate.revealed}
            progress={frame.gate.progress}
            maxHeight={doc.canvas.height * PANEL_GEOMETRY.maxHeightRatio}
          />
        )}
      </>
    );

    return (
      <div className={cn('w-full', className)} data-jr-video="" data-video-scene={sc.scene.id}>
        {/* aucune transition : chaque image ne dépend que de son instant */}
        <style>{NO_MOTION_CSS}</style>
        <AnimationStage ref={stageRef} anim={anim} assetUrl={assetUrl} overlay={overlay} />
      </div>
    );
  },
);
